import { useEffect, useState } from "react";
import { applyProofEdit, categoryKey, cleanCategories, editsCatalog, kindOf } from "@speisekarte/core";
import type { DishKind } from "@speisekarte/core";
import { KindSwitch, drinksInPlay } from "./Dishes.tsx";
import { GRIP_PATH, useSortable } from "../sortable.ts";
import { useDraft, useKept } from "../kept.ts";
import { api, messageOf, store, useStore } from "../store.ts";
import { ProofBox, openEdits } from "./Proof.tsx";
import type { OpenEdit } from "./Proof.tsx";
import { PhraseLine } from "./TextWork.tsx";

interface Row {
  /** Stable key while editing. */
  key: number;
  name: string;
  /** The name it has on the server; undefined for a new one. */
  from?: string;
}

const rowsOf = (list: string[]): Row[] => list.map((name, key) => ({ key, name, from: name }));
let nextKey = 10_000;

/**
 * „Kategorien“: the business's list (S12), saved as a whole, each change as one leaves the field (no
 * „Speichern“, Martin, 01.11.2026: it looked as if changes were saved before). Renaming renames at every dish; one in
 * use cannot go (the number on the right says how many dishes have it). The order, by the grip, is
 * the order of the filters and of „Abschnitte aus Kategorien“. Drinks (S22) have a list of their
 * own, behind a switch that shows only when the business has drinks.
 */
export function Categories({ kind = "food" }: { kind?: DishKind }) {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const drink = kind === "drink";
  const list = drink ? data.drinkCategories : data.categories;

  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const saved = JSON.stringify(list);
  // The list as being changed, kept while one looks elsewhere (S17); anew when it was saved.
  const [rows, setRows] = useDraft<Row[]>(drink ? "categories:drink" : "categories", saved, () => rowsOf(list));
  // New ones apart until they have a name: saved, they join the list at its end.
  const [fresh, setFresh] = useKept<Row[]>(drink ? "categories-new:drink" : "categories-new", []);
  useEffect(() => setState({}), [kind]);
  const used = new Map<string, number>();
  for (const d of data.dishes) if (d.category && kindOf(d) === kind) used.set(d.category, (used.get(d.category) ?? 0) + 1);
  // „Deutsch prüfen“ (S47): a correction taken renames, at every dish once saved.
  const proofEdits = openEdits(
    data,
    rows.flatMap((r) => (r.from ? [{ key: categoryKey(kind, r.from), text: r.name, where: `„${r.from}“` }] : [])),
  );
  const take = (e: OpenEdit) => void save(rows.map((r) => (r.from && e.key === categoryKey(kind, r.from) ? { ...r, name: applyProofEdit(r.name, e.edit) } : r)));
  const sort = useSortable(
    (from, to) => {
      const next = [...rows];
      const [row] = next.splice(from.index, 1);
      next.splice(to.index, 0, row!);
      void save(next);
    },
    (from, by) => {
      const at = from.index + by;
      if (at < 0 || at >= rows.length) return;
      const next = [...rows];
      [next[from.index], next[at]] = [next[at]!, next[from.index]!];
      void save(next);
    },
  );

  /** The list with the new ones that have a name; nothing when it is as saved. */
  async function save(next: Row[], pending = fresh) {
    const all = [...next, ...pending.filter((r) => r.name.trim())];
    setRows(next);
    if (JSON.stringify(all.map((r) => [r.name, r.from])) === JSON.stringify(list.map((c) => [c, c]))) return;
    const entries = all.map((r) => (r.from && r.from !== r.name ? { name: r.name, from: r.from } : { name: r.name }));
    const clean = cleanCategories(entries);
    if ("error" in clean) return setState({ error: clean.error });
    try {
      await store.change(() => api(`/api/categories${drink ? "?kind=drink" : ""}`, { method: "PUT", body: { categories: clean.value } }));
      setFresh(pending.filter((r) => !r.name.trim()));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }

  return (
    <div>
      <p>
        <a className="back" href={drink ? "#/getraenke" : "#/kataloge"}>{drink ? "← Getränke" : "← Kataloge"}</a>
      </p>
      <section className="card">
        <h2>Kategorien</h2>
        {drinksInPlay(data) && <KindSwitch kind={kind} food="#/kategorien" drink="#/kategorien?art=getraenke" />}
        <p className="hint">
          {drink
            ? "Jedes Getränk hat eine Kategorie aus dieser Liste (oder keine), etwa „Biere“ oder „Weine / Rotweine“. Aus der ersten Ebene macht die Speisekarte die Abschnitte der Getränke, in dieser Reihenfolge."
            : "Jede Speise hat eine Kategorie aus dieser Liste (oder keine). Ebenen mit „ / “ trennen, etwa „Warm / Meeresfrüchte“; aus der ersten Ebene macht „Abschnitte aus Kategorien“ die Abschnitte einer Speisekarte, in dieser Reihenfolge."}
        </p>
        <ProofBox edits={proofEdits} take={take} canEdit={canEdit} />
        <ul className="list category-list" data-sort-root>
          <li data-sort-group="list" style={{ border: "none" }} className={sort.groupClass("list", rows.length)}>
            {rows.map((r, i) => {
              const n = r.from ? (used.get(r.from) ?? 0) : 0;
              return (
                <div key={r.key} className={`category-row ${sort.rowClass({ group: "list", index: i })}`} data-sort-row data-testid="category-row">
                  <input value={r.name} onChange={(e) => [setRows(rows.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x))), setState({})]} onBlur={() => void save(rows)} readOnly={!canEdit} aria-label="Name der Kategorie" placeholder={drink ? "z. B. Weine / Rotweine" : "z. B. Warm / Fisch"} />
                  <span className="muted small count" title={drink ? "So viele Getränke haben diese Kategorie" : "So viele Speisen haben diese Kategorie"}>
                    {n || ""}
                  </span>
                  {canEdit && (
                    <>
                      <button className="link danger" disabled={n > 0} onClick={() => void save(rows.filter((x) => x.key !== r.key))} aria-label={`${r.name} entfernen`} title={n > 0 ? (drink ? "Wird noch von Getränken benutzt" : "Wird noch von Speisen benutzt") : "Entfernen"}>
                        ✕
                      </button>
                      <span {...sort.handle({ group: "list", index: i }, r.name || "Kategorie")}>
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                          <path d={GRIP_PATH} stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
                        </svg>
                      </span>
                    </>
                  )}
                  {/* Its translation right here (S61 § 4.1), once saved under this name. */}
                  {r.from && r.name === r.from && <PhraseLine text={r.from} data={data} />}
                </div>
              );
            })}
          </li>
          {fresh.length > 0 && (
            <li style={{ border: "none" }}>
              {fresh.map((r) => (
                <div key={r.key} className="category-row unsaved" data-testid="category-new">
                  <input value={r.name} onChange={(e) => [setFresh(fresh.map((x) => (x.key === r.key ? { ...x, name: e.target.value } : x))), setState({})]} onBlur={() => void save(rows)} aria-label="Name der Kategorie" placeholder={drink ? "z. B. Weine / Rotweine" : "z. B. Warm / Fisch"} autoFocus />
                  <span className="count" />
                  <button className="link danger" onClick={() => setFresh(fresh.filter((x) => x.key !== r.key))} aria-label="Neue Kategorie entfernen" title="Entfernen">
                    ✕
                  </button>
                  {/* Where the grip will be once saved: the columns stay. */}
                  <span className="grip" style={{ visibility: "hidden" }} aria-hidden="true" />
                </div>
              ))}
            </li>
          )}
        </ul>
        {fresh.length > 0 && (
          <p className="warn" data-testid="categories-unsaved">
            Noch nicht gespeichert: Eine neue Kategorie wird gespeichert, sobald sie einen Namen hat.
          </p>
        )}
        {canEdit && (
          <div className="buttons">
            <button onClick={() => [setFresh([...fresh, { key: nextKey++, name: "" }]), setState({})]} data-testid="add-category">
              + Kategorie
            </button>
          </div>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
        <p className="hint">{drink ? "Jede Änderung wird gespeichert, sobald du das Feld verlässt. Umbenennen ändert die Kategorie bei allen Getränken. Eine Kategorie, die noch bei Getränken steht (Zahl rechts), lässt sich erst entfernen, wenn dort eine andere gewählt ist." : "Jede Änderung wird gespeichert, sobald du das Feld verlässt. Umbenennen ändert die Kategorie bei allen Speisen. Eine Kategorie, die noch bei Speisen steht (Zahl rechts), lässt sich erst entfernen, wenn dort eine andere gewählt ist."}</p>
      </section>
    </div>
  );
}
